import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabaseClient';
import { IArticle } from '../../services/articleService';
import { useNavigate } from 'react-router-dom';

const AdminFeaturedArticlePage: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<IArticle[]>([]);
  const [featuredId, setFeaturedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!profile?.is_admin) {
      navigate('/');
      return;
    }
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all articles
        const { data: articlesData, error: articlesError } = await supabase
          .from('articles')
          .select('*')
          .order('created_at', { ascending: false });
        if (articlesError) throw articlesError;
        setArticles(articlesData || []);
        // Fetch featured article id
        const { data: settingsData, error: settingsError } = await supabase
          .from('settings')
          .select('featured_article_id')
          .single();
        if (settingsError) throw settingsError;
        setFeaturedId(settingsData?.featured_article_id || null);
      } catch (err: any) {
        setError(err.message || 'Error loading data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [profile, navigate]);

  const setFeatured = async (articleId: number) => {
    setSaving(true);
    setError(null);
    try {
      const { error } = await supabase
        .from('settings')
        .update({ featured_article_id: articleId })
        .eq('id', 1);
      if (error) throw error;
      setFeaturedId(articleId);
    } catch (err: any) {
      setError(err.message || 'Failed to set featured article');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;
  if (error) return <div className="p-8 text-red-600">{error}</div>;

  return (
    <div className="utility-page admin-page max-w-3xl mx-auto p-8">
      <h1 className="text-2xl font-bold mb-6">Admin: Featured Article</h1>
      <div className="overflow-x-auto"><table className="min-w-full border">
        <thead>
          <tr className="bg-white">
            <th className="py-2 px-4 border">Title</th>
            <th className="py-2 px-4 border">Status</th>
            <th className="py-2 px-4 border">Actions</th>
          </tr>
        </thead>
        <tbody>
          {articles.map(article => (
            <tr key={article.id} className={featuredId === article.id ? 'bg-green-50' : ''}>
              <td className="py-2 px-4 border">{article.title}</td>
              <td className="py-2 px-4 border">{article.status}</td>
              <td className="py-2 px-4 border">
                {featuredId === article.id ? (
                  <span className="text-green-700 font-semibold">Featured</span>
                ) : (
                  <button
                    className="bg-black text-white px-3 py-1 rounded hover:bg-gray-800 disabled:opacity-50"
                    onClick={() => setFeatured(article.id)}
                    disabled={saving}
                  >
                    Set as Featured
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </div>
  );
};

export default AdminFeaturedArticlePage; 